export type MovementMode = "free" | "lanes";

export interface GameSettings {
  movementMode: MovementMode;
  /** Theme/background music in menus + podiums. Off by default — the game is
   *  silent of music unless the player turns this on. (The final podium's win
   *  track plays regardless; it's a one-off celebration.) */
  musicEnabled: boolean;
  musicVolume: number;
  audioVolume: number;
  movementSensitivity: number;
  cameraSensitivity: number;
  controllerDeadzone: number;
  /** Hidden accessibility mode for very young players. Auto-runs and auto-
   *  jumps; the joystick / arrows only steer left/right. Toggle revealed by
   *  long-pressing the settings header (see LocalSettings). */
  assistMode: boolean;
  /** Show a small on-screen FPS counter during gameplay (for checking
   *  performance on older tablets). */
  showFps: boolean;
}

const STORAGE_KEY = "wr_game_settings_v1";
export const GAME_SETTINGS_CHANGED = "wr-game-settings-changed";

export const DEFAULT_GAME_SETTINGS: GameSettings = {
  movementMode: "free",
  musicEnabled: false,
  musicVolume: 0.5,
  audioVolume: 1,
  movementSensitivity: 1.0,
  cameraSensitivity: 1.0,
  controllerDeadzone: 0.18,
  assistMode: false,
  showFps: false,
};

function clampVolume(value: unknown, fallback: number): number {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, Math.min(1, n));
}

function clampSensitivity(value: unknown, fallback: number): number {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0.25, Math.min(1.5, n));
}

function clampDeadzone(value: unknown, fallback: number): number {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0.05, Math.min(0.4, n));
}

export function loadGameSettings(): GameSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_GAME_SETTINGS;
    const parsed = JSON.parse(raw) as Partial<GameSettings>;
    return {
      movementMode: parsed.movementMode === "lanes" ? "lanes" : "free",
      musicEnabled: !!parsed.musicEnabled,
      musicVolume: clampVolume(parsed.musicVolume, DEFAULT_GAME_SETTINGS.musicVolume),
      audioVolume: clampVolume(parsed.audioVolume, DEFAULT_GAME_SETTINGS.audioVolume),
      movementSensitivity: clampSensitivity(parsed.movementSensitivity, DEFAULT_GAME_SETTINGS.movementSensitivity),
      cameraSensitivity: clampSensitivity(parsed.cameraSensitivity, DEFAULT_GAME_SETTINGS.cameraSensitivity),
      controllerDeadzone: clampDeadzone(parsed.controllerDeadzone, DEFAULT_GAME_SETTINGS.controllerDeadzone),
      assistMode: !!parsed.assistMode,
      showFps: !!parsed.showFps,
    };
  } catch {
    return DEFAULT_GAME_SETTINGS;
  }
}

export function saveGameSettings(settings: GameSettings) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    window.dispatchEvent(new CustomEvent<GameSettings>(GAME_SETTINGS_CHANGED, { detail: settings }));
  } catch {
    /* ignore private-mode storage failures */
  }
}
